import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FolderOpen, Search, FileText, Upload, Download, Trash2, Eye, Loader2, Mail, Phone, ChevronLeft, Building2, MapPin } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import DocumentViewer from "./DocumentViewer";

const DOC_TYPES = ["Passport", "Driving License", "Aadhar Card", "PAN Card"];
const ROLE_LABEL = { legal_rep: "Legal Rep", accountant: "Accountant", broker: "Broker", caretaker: "Caretaker" };

export default function ClientProfiles() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/clients/directory"); setClients(data); }
      catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load clients"); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = clients.filter((c) => `${c.name} ${c.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="max-w-6xl" data-testid="client-profiles-page">
      <p className="text-xs uppercase tracking-[0.2em] font-mono text-emerald-700 font-semibold">Records</p>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight font-display text-slate-900">Client Files</h1>
      <p className="mt-2 text-sm text-slate-500">Open a client to review their profile and identity documents.</p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List */}
        <div className={`lg:col-span-1 ${selected ? "hidden lg:block" : ""}`}>
          <div className="flex items-center gap-2 bg-white border border-slate-300 rounded-lg px-3 focus-within:border-emerald-500">
            <Search className="h-4 w-4 text-slate-400" />
            <input data-testid="client-file-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients…"
              className="w-full bg-transparent py-2.5 text-sm text-slate-900 focus:outline-none" />
          </div>
          <div className="mt-3 space-y-1.5">
            {loading ? (
              <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center">No clients found.</p>
            ) : filtered.map((c) => (
              <button key={c.id} data-testid="client-file-item" onClick={() => setSelected(c.id)}
                className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-colors ${selected === c.id ? "border-emerald-300 bg-emerald-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <span className="h-9 w-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center font-semibold shrink-0">{(c.name || "?").charAt(0)}</span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-slate-800 truncate">{c.name}</span>
                  <span className="block text-xs text-slate-400 font-mono truncate">{c.email}</span>
                </span>
                {ROLE_LABEL[c.role] && <span data-testid="client-file-role-badge" className="ml-auto text-[10px] px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700 shrink-0">{ROLE_LABEL[c.role]}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          {selected ? <ClientDetail clientId={selected} onBack={() => setSelected(null)} />
            : <div className="hidden lg:flex h-full min-h-[300px] items-center justify-center border border-dashed border-slate-300 rounded-2xl text-slate-400">
                <div className="text-center"><FolderOpen className="h-8 w-8 mx-auto" /><p className="mt-2 text-sm">Select a client to view their file</p></div>
              </div>}
        </div>
      </div>
    </div>
  );
}

function ClientDetail({ clientId, onBack }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docType, setDocType] = useState("");
  const [expiry, setExpiry] = useState("");
  const [viewDoc, setViewDoc] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/clients/${clientId}/profile`); setProfile(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load client"); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file || !docType) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large (max 5MB)"); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post(`/clients/${clientId}/identity`, { doc_type: docType, file: reader.result, file_name: file.name, expiry_date: expiry });
        setProfile((p) => ({ ...p, identity_documents: [...p.identity_documents, data] }));
        setDocType(""); setExpiry(""); toast.success(`${data.doc_type} uploaded`);
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
      finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
  };

  const remove = async (id) => {
    try { await api.delete(`/clients/${clientId}/identity/${id}`); setProfile((p) => ({ ...p, identity_documents: p.identity_documents.filter((x) => x.id !== id) })); toast.success("Document deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete"); }
  };

  const download = (doc) => { const a = document.createElement("a"); a.href = doc.file; a.download = doc.file_name || doc.doc_type; a.click(); };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  if (!profile) return null;
  const canEdit = profile.can_edit;

  return (
    <div data-testid="client-detail">
      <button onClick={onBack} className="lg:hidden inline-flex items-center gap-1 text-sm text-slate-500 mb-3"><ChevronLeft className="h-4 w-4" /> Back</button>
      <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center gap-4">
        <span className="h-14 w-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center text-xl font-semibold text-emerald-700">{(profile.name || "?").charAt(0)}</span>
        <div className="min-w-0">
          <div className="text-lg font-semibold text-slate-900">{profile.name} {ROLE_LABEL[profile.role] && <span className="ml-2 align-middle text-xs px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700">{ROLE_LABEL[profile.role]}</span>}</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 mt-1">
            <span className="flex items-center gap-1.5"><Mail className="h-4 w-4" /> {profile.email}</span>
            {profile.phone && <span className="flex items-center gap-1.5" data-testid="client-file-phone"><Phone className="h-4 w-4" /> {profile.phone}</span>}
            {profile.company && <span className="flex items-center gap-1.5" data-testid="client-file-company"><Building2 className="h-4 w-4" /> {profile.company}</span>}
            {profile.address && <span className="flex items-center gap-1.5" data-testid="client-file-address"><MapPin className="h-4 w-4" /> {profile.address}</span>}
          </div>
        </div>
      </div>

      <div className="mt-6 p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-emerald-600" />
          <h2 className="text-lg font-semibold text-slate-900">Identity Documents</h2>
          {!canEdit && <span className="ml-auto text-xs font-mono text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">View only</span>}
        </div>

        {canEdit && (
          <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <label className="text-sm text-slate-600">Document type</label>
              <select data-testid="client-doc-type-select" value={docType} onChange={(e) => setDocType(e.target.value)}
                className="mt-1.5 w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20">
                <option value="">Select a document…</option>
                {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="sm:w-44">
              <label className="text-sm text-slate-600">Expiry date</label>
              <input type="date" data-testid="client-doc-expiry-input" value={expiry} onChange={(e) => setExpiry(e.target.value)}
                className="mt-1.5 w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
            </div>
            <label data-testid="client-doc-upload-button"
              className={`inline-flex items-center justify-center gap-2 font-semibold px-5 py-2.5 rounded-lg transition-all shadow-sm ${docType && !uploading ? "bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer active:scale-[0.98]" : "bg-slate-200 text-slate-400 cursor-not-allowed"}`}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
              <input type="file" accept="image/*,application/pdf" disabled={!docType || uploading} onChange={onFile} className="hidden" />
            </label>
          </div>
        )}

        <div className="mt-5 space-y-2" data-testid="client-doc-list">
          {profile.identity_documents.length === 0 ? (
            <p className="text-sm text-slate-400 py-4">No documents on file.</p>
          ) : profile.identity_documents.map((doc) => (
            <div key={doc.id} data-testid="client-doc-item" className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50">
              <FileText className="h-5 w-5 text-slate-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-800 truncate">{doc.doc_type}</div>
                <div className="text-xs text-slate-400 font-mono truncate">{doc.file_name}</div>
                {doc.expiry_date && (
                  <div className="text-xs mt-0.5 font-mono flex items-center gap-1.5">
                    <span className={new Date(doc.expiry_date) < new Date() ? "text-rose-600" : "text-slate-500"}>Expires {doc.expiry_date}</span>
                    {new Date(doc.expiry_date) < new Date() && <span className="px-1.5 py-0.5 rounded-full bg-rose-100 border border-rose-200 text-rose-700">Expired</span>}
                  </div>
                )}
              </div>
              <button onClick={() => setViewDoc(doc)} data-testid="client-doc-view" title="View"
                className="p-2 rounded-lg text-slate-500 hover:bg-white hover:text-emerald-700 transition-colors"><Eye className="h-4 w-4" /></button>
              {canEdit && (
                <button onClick={() => download(doc)} data-testid="client-doc-download" title="Download"
                  className="p-2 rounded-lg text-slate-500 hover:bg-white hover:text-emerald-700 transition-colors"><Download className="h-4 w-4" /></button>
              )}
              {canEdit && (
                <button onClick={() => remove(doc.id)} data-testid="client-doc-delete" title="Delete"
                  className="p-2 rounded-lg text-rose-500 hover:bg-white transition-colors"><Trash2 className="h-4 w-4" /></button>
              )}
            </div>
          ))}
        </div>
      </div>
      {viewDoc && <DocumentViewer doc={viewDoc} canDownload={canEdit} onClose={() => setViewDoc(null)} />}
    </div>
  );
}
